package com.siriusxm.pia.views.unifiedaggregator.smithy

import androidx.compose.runtime.*
import com.siriusxm.cmc.utils.getFloat
import com.siriusxm.cmc.utils.getString
import com.siriusxm.cmc.utils.toDurationHumanString
import com.siriusxm.cmc.utils.toLocalDateTimeString
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.*
import com.siriusxm.pia.views.accesscontrols.AccessControlsBadgeView
import com.siriusxm.pia.views.accesscontrols.impliedDefaultAccessControlVariant
import com.siriusxm.pia.views.accesscontrols.toAccessControlVariants
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import com.siriusxm.pia.views.unifiedaggregator.AggregatorStyles
import com.siriusxm.smithy4kt.*
import contentingestion.unifiedmodel.EntityAccessControl
import contentingestion.unifiedmodel.EntityAccessControls
import contentingestion.unifiedmodel.Playlist
import kotlinx.datetime.toLocalDateTime
import kotlinx.serialization.KSerializer
import kotlinx.serialization.json.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.Instant

open class CustomRenderer(shape: SmithyEntity) : SmithyViewer<SmithyEntity>(shape) {
    /**
     * Helper to decode a value
     */
    inline fun <reified T> decode(value: JsonElement?): T? {
        if (value == null) return null

        return smithyViewDecoder.decodeFromJsonElement<T>(value)
    }
}

abstract class CustomTypeRenderer<T>(shape: SmithyEntity, val serializer: KSerializer<T>) : CustomRenderer(shape) {
    @Composable
    override fun view(value: JsonElement?, options: SmithyViewOptions) {
        val typeValue = value?.let {
            try {
                smithyViewDecoder.decodeFromJsonElement(serializer, it)
            } catch (e: Exception) {
                console.error("Failed to decode: ${e.message}")
                null
            }
        }
        if (typeValue == null && value != null) {
            // Deserialization failed — show raw JSON as fallback
            Text(value.toString().take(500))
        } else {
            viewType(typeValue, options)
        }
    }

    @Composable
    abstract fun viewType(value: T?, options: SmithyViewOptions)
}

/**
 * A rendered that displays a map as a set of labels and content instead of a table
 */
class LabelledFieldsMapViewer(shape: SmithyEntity) : CustomRenderer(shape) {
    @Composable
    override fun view(value: JsonElement?, options: SmithyViewOptions) {
        val obj = value as? JsonObject ?: return
        val smithyMap = shape as? SmithyMap ?: return

        Div({ classes(AggregatorStyles.additionalNames) }) {
            obj.entries.forEach {
                Div({ classes(AggregatorStyles.additionalName) }) {
                    Div({
                        style {
                            fontSize(11.px)
                            color(SXMUI.subtleTextColor.value())
                        }
                    }) {
                        Text(it.key)
                    }
                    Div {
                        SmithyViewer(smithyMap.value, options) {}?.view(it.value, options)
                    }
                }
            }
        }

        super.view(value, options)
    }
}

/**
 * Renders the additional names component
 */
class NamesMapViewer(shape: SmithyEntity) : CustomRenderer(shape) {
    @Composable
    override fun view(value: JsonElement?, options: SmithyViewOptions) {
        val names = value?.jsonObject?.values?.mapNotNull { it as? JsonObject } ?: return

        Div({ classes(AggregatorStyles.additionalNames) }) {
            names.forEach {
                Div({ classes(AggregatorStyles.additionalName) }) {
                    Div({
                        style {
                            fontSize(11.px)
                            color(SXMUI.subtleTextColor.value())
                        }
                    }) {
                        Text(it.getString("type").orEmpty())
                    }
                    Div {
                        Text(it.getString("name").orEmpty())
                    }
                }
            }
        }
    }

    @Composable
    override fun edit(
        baseValue: JsonElement?,
        modification: JsonElement?,
        options: SmithyViewOptions
    ) {
        SmithyMapEditor(shape as SmithyMap).apply {
            onUpdate = this@NamesMapViewer.onUpdate
        }.edit(baseValue, modification, options)
    }
}

/**
 * Render a color as a swatch
 */
class ColorViewer(shape: SmithyEntity) : CustomRenderer(shape) {
    fun hexToRgb(hex: String): Triple<Int, Int, Int> {
        // Remove '#' if present.
        val cleanedHex = hex.removePrefix("#")

        // Validate that we have exactly 6 characters.
        require(cleanedHex.length == 6) { "Invalid hex color format." }

        val r = cleanedHex.substring(0, 2).toInt(16)
        val g = cleanedHex.substring(2, 4).toInt(16)
        val b = cleanedHex.substring(4, 6).toInt(16)

        return Triple(r, g, b)
    }

    @Composable
    override fun view(value: JsonElement?, options: SmithyViewOptions) {
        val hex = value?.getString("hex")
        val rgb = hex?.let {
            hexToRgb(it)
        } ?: return
        val opacity = value.getFloat("opacity") ?: 1.0f

        Div({
            style {
                backgroundColor(rgba(rgb.first, rgb.second, rgb.third, opacity))
                width(25.px)
                height(25.px)
                border(1.px, LineStyle.Solid, SXMUI.defaultDivider.value())
                display(DisplayStyle.Block)
            }
        })
        copyContentIcon(hex)
    }
}

class PlaylistViewer(shape: SmithyEntity) : CustomRenderer(shape) {
    @Composable
    override fun view(value: JsonElement?, options: SmithyViewOptions) {
        val playlistList = value?.let {
            smithyViewDecoder.decodeFromJsonElement<List<Playlist>>(it)
        } ?: return

        table<Playlist> {
            items(playlistList)

            column {
                content {
                    Text(it.playlistCode.name)
                }
            }
            column {
                content {
                    Text(it.originId.name)
                }
            }
            column {
                content {
                    Text(it.uriPath)
                }
            }
        }
    }

    @Composable
    override fun edit(
        baseValue: JsonElement?,
        modification: JsonElement?,
        options: SmithyViewOptions
    ) {
        (shape as? SmithyList)?.let { list ->
            SmithyListViewer(list).apply {
                onUpdate = this@PlaylistViewer.onUpdate
            }.edit(baseValue, modification, options)
        }
    }
}

class AccessControlViewer(shape: SmithyEntity) :
    CustomTypeRenderer<EntityAccessControls>(shape, EntityAccessControls.serializer()) {
    val defaultJson = Json {
        encodeDefaults = true
    }

    @Composable
    override fun viewType(value: EntityAccessControls?, options: SmithyViewOptions) {
        // Render with the standard access-control badge view (shared with the
        // entity header). In the data table there is room to show every variant,
        // so it is always expanded - each variant appears as a labeled badge row.
        //
        // No `key` is passed here (defaults to `variants`, content equality): with
        // alwaysExpanded = true the collapsed/expanded state is never read, so this is
        // dormant today. SmithyViewOptions carries no per-row identity to pass instead. If this
        // field ever becomes conditionally collapsible, thread a real row/entity id through as
        // the key — see AggregatorEntity.kt's header call site, which passes `key = id`.
        val resolved = value ?: EntityAccessControls(EntityAccessControl(true, true, true))
        val variants = (defaultJson.encodeToJsonElement(EntityAccessControls.serializer(), resolved) as? JsonObject)
            ?.toAccessControlVariants(includeImpliedDefault = true)
            ?.takeIf { it.isNotEmpty() }
            ?: listOf(impliedDefaultAccessControlVariant())
        AccessControlsBadgeView(variants, alwaysExpanded = true)
    }

    /**
     * For edits, just defer to the default editor for now.
     */
    @Composable
    override fun edit(baseValue: JsonElement?, modification: JsonElement?, options: SmithyViewOptions) {
        LaunchedEffect(Unit) {
            if ((modification ?: JsonNull) == JsonNull) {
                onUpdate(populateDefaults(baseValue, modification, options))
            }
        }
        SmithyStructEditor(shape as SmithyStructure).apply {
            onUpdate = {
                this@AccessControlViewer.onUpdate(it)
            }
        }.edit(baseValue, modification, options)
    }


    override fun populateDefaults(
        baseValue: JsonElement?,
        modification: JsonElement?,
        options: SmithyViewOptions
    ): JsonElement? {
        val defaults = EntityAccessControls(
            default = EntityAccessControl(true, true, true),
        )
        return defaultJson.encodeToJsonElement(defaults)
    }
}

/**
 * Viewer for built-in Smithy Timestamp fields. Unlike the iso8601Timestamp typedef
 * (which is a SmithyString), SmithyTimestamp extends SmithyShape directly, so we
 * cannot delegate editing to SmithyStringEditor.
 */
class SmithyTimestampViewer(shape: SmithyTimestamp) : SmithyViewer<SmithyTimestamp>(shape) {
    @Composable
    override fun view(value: JsonElement?, options: SmithyViewOptions) {
        val str = (value as? JsonPrimitive)?.contentOrNull ?: return

        val ts = try {
            Instant.parse(str)
        } catch (_: Throwable) {
            return
        }

        // Render in the explicit override zone when set, otherwise the globally-selected zone
        // (header picker). IANA-zone projection goes through the Intl-based helper since
        // TimeZone.of("America/...") is unavailable on Kotlin/JS.
        val dateTime = options.timezone?.let { ts.toLocalDateTime(it) }
            ?: ts.toLocalDateTimeIn(currentTimeZoneId())
        Text(dateTime.toLocalDateTimeString())
    }
}

/**
 * Viewer/editor for an iso8601 date time typedef (backed by SmithyString)
 */
class Iso8601TimestampViewer(shape: SmithyEntity) : CustomRenderer(shape) {
    @Composable
    override fun view(value: JsonElement?, options: SmithyViewOptions) {
        val str = (value as? JsonPrimitive)?.contentOrNull ?: return

        val ts = try {
            Instant.parse(str)
        } catch (_: Throwable) {
            return
        }

        // Render in the explicit override zone when set, otherwise the globally-selected zone
        // (header picker). IANA-zone projection goes through the Intl-based helper since
        // TimeZone.of("America/...") is unavailable on Kotlin/JS.
        val dateTime = options.timezone?.let { ts.toLocalDateTime(it) }
            ?: ts.toLocalDateTimeIn(currentTimeZoneId())
        Text(dateTime.toLocalDateTimeString())
    }

    @Composable
    override fun edit(baseValue: JsonElement?, modification: JsonElement?, options: SmithyViewOptions) {
        SmithyStringEditor(shape as SmithyString).edit(baseValue, modification, options)
    }
}

class EntityTypeViewer(val aggregatorService: AggregatorService, shape: SmithyEntity) : CustomRenderer(shape) {
    @Composable
    override fun view(value: JsonElement?, options: SmithyViewOptions) {
        val typeId = (value as? JsonPrimitive)?.contentOrNull ?: return

        val entityType = aggregatorService.entityType(typeId)
        val display = entityType?.name ?: typeId
        A(href = "#aggregator/types/entities/${typeId}") {
            Text(display)
        }
    }
}

/**
 * Displays milliseconds as a duration in a more human-readable format.
 * Negative values (e.g., -1 meaning "not set") are shown as the raw number.
 */
class MillisecondsViewer(shape: SmithyEntity) : CustomRenderer(shape) {
    @Composable
    override fun view(value: JsonElement?, options: SmithyViewOptions) {
        val milliseconds = (value as? JsonPrimitive)?.intOrNull ?: return

        if (milliseconds < 0) {
            Text("$milliseconds")
        } else {
            val duration = milliseconds.milliseconds
            Text(duration.toDurationHumanString(true))
        }
        copyContentIcon(milliseconds.toString())
    }
}
